<template>
  <div v-if="!loading && schedule">
    <h1 class="main-header">
      <translate
        translate-context="email_schedules"
        :translate-params="{name: getJobTypeLabel(schedule.schedule_type)}">
        Edit Email Schedule: %{name}
      </translate>
    </h1>

    <CCard>
      <CCardHeader>
        <div class="card-header-actions">
          <CButton
            variant="outline"
            color="success"
            @click="onReset"
            class="mr-2">
            <translate translate-context="form">Reset</translate>
          </CButton>
          <CButton color="primary" @click="onClickSave">
            <translate translate-context="form">Save</translate>
          </CButton>
        </div>
      </CCardHeader>
      <CCardBody>
        <EmailScheduleForm
          ref="form"
          :data="schedule"
          @save="onSave"
          edit/>
      </CCardBody>
    </CCard>

    <!-- Debug Info Section -->
    <CCard v-if="canDebugInfo">
      <CCardHeader>
        <strong><translate translate-context="email_schedules.debug">Debug Information</translate></strong>
        <div class="card-header-actions">
          <CButton
            v-if="canForceRestart"
            variant="outline"
            color="warning"
            size="sm"
            :disabled="forceRestartLoading"
            @click="onForceRestart">
            <CSpinner v-if="forceRestartLoading" size="sm" class="mr-1"/>
            <translate translate-context="email_schedules.debug">Force Restart</translate>
          </CButton>
          <CButton
            variant="outline"
            color="info"
            size="sm"
            class="ml-2"
            :disabled="debugLoading"
            @click="loadDebugInfo">
            <CSpinner v-if="debugLoading" size="sm" class="mr-1"/>
            <translate translate-context="email_schedules.debug">Refresh</translate>
          </CButton>
        </div>
      </CCardHeader>
      <CCardBody v-if="debugInfo">
        <CRow>
          <CCol md="6">
            <dl class="row mb-0">
              <dt class="col-sm-5"><translate translate-context="email_schedules.debug">Emails Sent</translate></dt>
              <dd class="col-sm-7">{{ debugInfo.email_sent_count || 0 }}</dd>

              <dt class="col-sm-5"><translate translate-context="email_schedules.debug">Last Run At</translate></dt>
              <dd class="col-sm-7">{{ formatDateTime(debugInfo.last_run_at) }}</dd>
            </dl>
          </CCol>
          <CCol md="6">
            <dl class="row mb-0">
              <dt class="col-sm-5"><translate translate-context="email_schedules.debug">Last Completed At</translate></dt>
              <dd class="col-sm-7">{{ formatDateTime(debugInfo.last_completed_at) }}</dd>
            </dl>
          </CCol>
        </CRow>

        <!-- Last Run Info -->
        <div v-if="debugInfo.last_run_info" class="mt-3">
          <h6><translate translate-context="email_schedules.debug">Last Run Info</translate></h6>
          <CAlert color="secondary" class="mb-0">
            <pre class="mb-0 debug-json">{{ formatJson(debugInfo.last_run_info) }}</pre>
          </CAlert>
        </div>
        <div v-else class="mt-3">
          <CAlert color="warning" class="mb-0">
            <translate translate-context="email_schedules.debug">No run information available yet.</translate>
          </CAlert>
        </div>
      </CCardBody>
      <CCardBody v-else>
        <div class="text-center py-3">
          <CSpinner color="primary"/>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import EmailScheduleForm from './components/EmailScheduleForm.vue'
import { RecordNotFound, SuccessNotification } from '@/domain/core/exception/exceptions'
import { getJobTypeLabel } from '@/domain/core/constant/emailJobTypes'
import moment from 'moment'

export default {
  name: 'EditEmailSchedule',
  components: {
    EmailScheduleForm
  },
  data() {
    return {
      loading: true,
      debugLoading: false,
      forceRestartLoading: false
    }
  },
  computed: {
    ...mapGetters('emailSchedules', ['schedule', 'debugInfo']),
    ...mapGetters('login', ['hasPerm']),

    scopeType() {
      return this.$route.meta.scopeType || 'global'
    },

    scopeId() {
      if (this.scopeType === 'pica-service') {
        return this.$route.params.picaServiceId
      }
      if (this.scopeType === 'contract-manager') {
        return this.$route.params.contractManagerId
      }
      if (this.scopeType === 'event-owner') {
        return this.$route.params.eventOwnerId
      }
      return null
    },

    listRouteName() {
      const names = {
        'global': 'admin.emailSchedules.list',
        'contract-manager': 'contractManager.emailSchedules.list',
        'event-owner': 'eventOwner.emailSchedules.list',
        'pica-service': 'event.emailSchedules.list'
      }
      return names[this.scopeType]
    },

    canDebugInfo() {
      const perms = {
        'global': 'admin.email_scheduler.debug_info',
        'contract-manager': 'contract_manager.email_scheduler.debug_info',
        'event-owner': 'event_owner.email_scheduler.debug_info',
        'pica-service': 'email_scheduler.debug_info'
      }
      return this.hasPerm(perms[this.scopeType])
    },

    canForceRestart() {
      const perms = {
        'global': 'admin.email_scheduler.force_restart',
        'contract-manager': 'contract_manager.email_scheduler.force_restart',
        'event-owner': 'event_owner.email_scheduler.force_restart',
        'pica-service': 'email_scheduler.force_restart'
      }
      return this.hasPerm(perms[this.scopeType])
    }
  },
  async mounted() {
    try {
      // Set scope first
      await this.setScope({ type: this.scopeType, id: this.scopeId })

      // Then fetch the schedule
      await this.fetchEmailSchedule(this.$route.params.scheduleId)

      // Load debug info if user has permission
      if (this.canDebugInfo) {
        this.loadDebugInfo()
      }
    } catch (e) {
      if (e instanceof RecordNotFound) {
        await this.$router.push({ name: this.listRouteName })
        this.notifyError(this.$pgettext('email_schedules', 'Schedule not found'))
      }
    }
    this.loading = false
  },
  methods: {
    ...mapActions('emailSchedules', ['setScope', 'fetchEmailSchedule', 'updateEmailSchedule', 'fetchDebugInfo', 'forceRestartEmailSchedule']),

    getJobTypeLabel(jobType) {
      return getJobTypeLabel(jobType)
    },

    onClickSave() {
      this.$refs.form.onSave()
    },

    onReset() {
      this.$refs.form.onReset()
    },

    async onSave(data) {
      try {
        await this.updateEmailSchedule({
          scheduleId: this.$route.params.scheduleId,
          data: data
        })
        await this.$router.push({ name: this.listRouteName })
        throw new SuccessNotification(
          this.$pgettext('email_schedules', 'Email schedule updated successfully!')
        )
      } catch (error) {
        if (error instanceof SuccessNotification) {
          throw error
        }
        // Handle validation errors
        if (error.response && error.response.status === 400) {
          this.notifyError(this.$pgettext('email_schedules', 'Please check the form for errors'))
        } else {
          this.notifyError(this.$pgettext('email_schedules', 'Failed to update schedule'))
        }
      }
    },

    async loadDebugInfo() {
      this.debugLoading = true
      try {
        await this.fetchDebugInfo(this.$route.params.scheduleId)
      } catch (error) {
        this.notifyError(this.$pgettext('email_schedules.debug', 'Failed to load debug info'))
      }
      this.debugLoading = false
    },

    onForceRestart() {
      this.confirm({
        title: this.$pgettext('email_schedules.debug', 'Force Restart Schedule'),
        message: this.$pgettext('email_schedules.debug', 'This will reset the schedule and queue it for immediate execution. Are you sure?'),
        cb_confirm: async () => {
          this.forceRestartLoading = true
          try {
            await this.forceRestartEmailSchedule(this.$route.params.scheduleId)
            this.notifySuccess(this.$pgettext('email_schedules.debug', 'Schedule restarted successfully'))
            // Reload debug info after restart
            await this.loadDebugInfo()
          } catch (error) {
            this.notifyError(this.$pgettext('email_schedules.debug', 'Failed to restart schedule'))
          }
          this.forceRestartLoading = false
        }
      })
    },

    formatDateTime(date) {
      if (!date) return '-'
      return moment(date).format('DD/MM/YYYY HH:mm:ss')
    },

    formatJson(data) {
      if (!data) return ''
      try {
        return JSON.stringify(data, null, 2)
      } catch (e) {
        return String(data)
      }
    }
  }
}
</script>

<style scoped>
.main-header {
  margin-bottom: 1.5rem;
}

.debug-json {
  background-color: transparent;
  font-size: 0.85rem;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 400px;
  overflow-y: auto;
}
</style>
